import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useUser } from "../UserContext.js";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import config from "../config"; // Import the config file

const StudentLogin = () => {
  const ICE_LOGO = require("../assets/carr_1.jpg");
  const navigate = useNavigate();
  const { setUser } = useUser();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [className, setClassName] = useState(""); // Add state for Class
  const [errors, setErrors] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Check if the user is already logged in
  useEffect(() => {
    try {
      const userFromStorage = localStorage.getItem("user");
      if (userFromStorage) {
        const parsedUser = JSON.parse(userFromStorage);
        setUser(parsedUser);
        setIsLoggedIn(true);
        navigate(`/dashboard/${parsedUser.id || parsedUser.studentId}`);
      }
    } catch (error) {
      console.error("Failed to parse user from localStorage:", error);
      localStorage.removeItem("user"); // Clear invalid data
    }
  }, [setUser, navigate]);

  // Handlers
  const handleEmailChange = (event) => setEmail(event.target.value);
  const handlePasswordChange = (event) => setPassword(event.target.value);
  const handleClassChange = (event) => setClassName(event.target.value); // Handle Class field change
  const togglePassword = () => setShowPassword((prev) => !prev);

  // Validate form fields
  const validateFields = () => {
    const errors = {};
    if (!email) errors.email = "Email is required.";
    if (!password) errors.password = "Password is required.";
    if (!className) errors.className = "Class is required."; // Validate Class
    return errors;
  };

  // Handle login
  const handleLogin = async (event) => {
    event.preventDefault();
    setErrors({}); // Clear previous errors

    const fieldErrors = validateFields();
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    try {
      // Fetch student data from JSON server
      const response = await fetch(`${config.apiEndpoint}/${className}`);
      if (!response.ok) {
        throw new Error("Failed to fetch student data from the server.");
      }

      const students = await response.json();
      if (!Array.isArray(students)) {
        throw new Error("Invalid data format from the server.");
      }

      // Match student credentials
      const student = students.find(
        (s) => s.Email === email && s.Password === password
      );

      if (!student) {
        throw new Error("Invalid email, password, or class.");
      }

      // Set user in context and localStorage
      setUser(student);
      setIsLoggedIn(true);
      localStorage.setItem("user", JSON.stringify(student));

      // Redirect to dashboard
      navigate(`/dashboard/${student.id || student.studentId}`);
    } catch (error) {
      console.error("Login error:", error);
      setErrors({ global: error.message });
    }
  };

  return (
    <div className="login flex flex-col items-center justify-center text-center p-6 md:p-10 lg:p-24 h-screen bg-gradient-to-r from-yellow-500 to-yellow-700">
      {!isLoggedIn && (
        <form
          className="w-full max-w-sm md:max-w-md lg:max-w-lg mx-auto p-6 md:p-8 lg:p-10 rounded-xl space-y-6 lg:space-y-8 bg-white shadow-lg"
          onSubmit={handleLogin}
        >
          <h1 className="text-2xl lg:text-3xl font-bold">LOGIN</h1>

          {/* Email Field */}
          <input
            className="w-full p-3 md:p-4 text-black border border-yellow-700 bg-gray-200 rounded-lg focus:outline-none focus:border-teal-700"
            onChange={handleEmailChange}
            value={email}
            type="email"
            placeholder="Enter Email"
          />
          {errors.email && (
            <p className="text-red-700 text-sm">{errors.email}</p>
          )}

          {/* Password Field */}
          <div className="relative">
            <input
              className="w-full p-3 md:p-4 text-black border border-yellow-700 bg-gray-200 rounded-lg focus:outline-none focus:border-teal-700"
              onChange={handlePasswordChange}
              value={password}
              type={showPassword ? "text" : "password"}
              placeholder="Enter Password"
            />
            <span
              className="absolute inset-y-0 right-3 flex items-center cursor-pointer text-gray-500"
              onClick={togglePassword}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>
          {errors.password && (
            <p className="text-red-700 text-sm">{errors.password}</p>
          )}

          {/* Class Field */}
          <input
            className="w-full p-3 md:p-4 text-black border border-yellow-700 bg-gray-200 rounded-lg focus:outline-none focus:border-teal-700"
            onChange={handleClassChange}
            value={className}
            type="text"
            placeholder="Enter Class"
          />
          {errors.className && (
            <p className="text-red-700 text-sm">{errors.className}</p>
          )}

          {/* Global Error Message */}
          {errors.global && (
            <p className="text-red-700 font-bold text-sm">{errors.global}</p>
          )}

          {/* Login Button */}
          <button
            className="w-full p-3 md:p-4 bg-yellow-500 rounded-lg font-bold hover:bg-yellow-700 transition-colors"
            type="submit"
          >
            LOGIN
          </button>
        </form>
      )}
    </div>
  );
};

export default StudentLogin;
